import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Timer, AlertTriangle, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eyebrow } from "./Common";
import { TRADES, CITIES } from "@/lib/hz";

const STEPS = ["Oficio", "Cuándo y dónde", "Tu trabajo"];

export const QuickQuote = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ oficio: "", urgencia: "normal", ciudad: "Madrid", titulo: "" });

  const finish = () => {
    const qs = new URLSearchParams({ oficio: f.oficio, urgencia: f.urgencia, ciudad: f.ciudad });
    if (f.titulo.trim()) qs.set("titulo", f.titulo.trim());
    nav(`/crear-solicitud?${qs.toString()}`);
  };

  const urg = (v, Icon, title, text) => (
    <button type="button" onClick={() => setF({ ...f, urgencia: v })} data-testid={`qq-urgency-${v}`}
      className={`flex flex-1 items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${f.urgencia === v ? (v === "urgente" ? "border-rose-500 bg-rose-50" : "border-orange-500 bg-orange-50") : "border-slate-200 hover:border-slate-300"}`}>
      <Icon className={`mt-0.5 size-5 ${f.urgencia === v ? (v === "urgente" ? "text-rose-600" : "text-orange-600") : "text-slate-400"}`} />
      <span><span className="block font-semibold">{title}</span><span className="text-xs text-slate-500">{text}</span></span>
    </button>
  );

  return (
    <section className="relative z-10 mx-auto -mt-10 max-w-6xl px-4" data-testid="quick-quote">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Eyebrow>Presupuesto en 1 minuto</Eyebrow>
            <h2 className="mt-1 font-heading text-2xl font-extrabold tracking-tight text-slate-900">Dinos qué necesitas y te llegan presupuestos</h2>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-500"><Timer className="size-4 text-orange-600" />Paso {step + 1} de 3 · {STEPS[step]}</div>
        </div>
        <div className="mt-4 flex gap-1.5">{STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-orange-500" : "bg-slate-200"}`} />)}</div>

        <div className="mt-6 min-h-[132px]">
          {step === 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
              {TRADES.map((t) => (
                <button key={t.id} type="button" data-testid={`qq-trade-${t.id}`} onClick={() => { setF({ ...f, oficio: t.id }); setStep(1); }}
                  className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-4 text-sm font-semibold transition-[border-color,transform] hover:-translate-y-0.5 ${f.oficio === t.id ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-orange-300"}`}>
                  <span className={`grid size-10 place-items-center rounded-xl ${t.tile}`}><t.icon className="size-5" /></span>{t.name}
                </button>
              ))}
            </div>
          )}
          {step === 1 && (
            <div className="grid gap-4 md:grid-cols-[1fr_1fr_220px]">
              {urg("normal", CalendarClock, "Sin prisa", "Comparo presupuestos con calma")}
              {urg("urgente", AlertTriangle, "Es urgente", "Necesito a alguien cuanto antes")}
              <Select value={f.ciudad} onValueChange={(v) => setF({ ...f, ciudad: v })}>
                <SelectTrigger className="h-full min-h-12" data-testid="qq-city-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="qq-title">En una frase, ¿qué hay que hacer?</label>
              <Input id="qq-title" autoFocus value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} onKeyDown={(e) => e.key === "Enter" && finish()}
                placeholder="Ej. Gotea el grifo de la cocina" className="h-12 text-base" data-testid="qq-title-input" />
              <p className="text-xs text-slate-500">En el siguiente paso podrás añadir detalles y fotos.</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="qq-back-btn"><ArrowLeft />Atrás</Button>
          {step < 2
            ? <Button className="rounded-xl" disabled={!f.oficio} onClick={() => setStep(step + 1)} data-testid="qq-next-btn">Siguiente <ArrowRight /></Button>
            : <Button size="lg" className="rounded-xl" onClick={finish} data-testid="qq-finish-btn">Pedir presupuesto <ArrowRight /></Button>}
        </div>
      </div>
    </section>
  );
};
